import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField,
  CircularProgress, Alert, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Checkbox, Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { authedFetch } from '../utils/authedFetch';

const FUNCTIONS_BASE = 'https://us-central1-torch-beach-app.cloudfunctions.net';

export default function TournamentRegistrationDetail() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [reg, setReg] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [issueRefund, setIssueRefund] = useState(true);
  const [newDivision, setNewDivision] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [partnerFirstName, setPartnerFirstName] = useState('');
  const [partnerLastName, setPartnerLastName] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [partnerPhone, setPartnerPhone] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => { loadData(); }, [rid]);

  async function loadData() {
    try {
      const snap = await getDoc(doc(db, 'registrations', rid));
      if (!snap.exists()) { setLoading(false); return; }
      const r = { id: snap.id, ...snap.data() };
      setReg(r);
      setFirstName(r.first_name || ''); setLastName(r.last_name || '');
      setPartnerFirstName(r.partner_first_name || '');
      setPartnerLastName(r.partner_last_name || '');
      setPartnerEmail(r.partner_email || '');
      setPartnerPhone(r.partner_phone || '');
      setPartnerName(r.partner_name || '');
      setEmail(r.email || '');
      setPhone(r.phone || ''); setNewDivision(r.division || ''); setPaymentStatus(r.payment_status || '');
      const tSnap = await getDoc(doc(db, 'tournaments', r.tournament_id));
      if (tSnap.exists()) {
        const t = tSnap.data();
        const divs = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
        setDivisions(divs);
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function savePlayer(e) {
    e.preventDefault();
    setSaving(true);
    try {
      // Keep partner_name synced for legacy displays (table listings etc.)
      const derivedPartnerName = (partnerFirstName || partnerLastName)
        ? `${partnerFirstName} ${partnerLastName}`.trim()
        : partnerName;
      const updateData = {
        first_name: firstName,
        last_name: lastName,
        partner_first_name: partnerFirstName,
        partner_last_name: partnerLastName,
        partner_email: partnerEmail,
        partner_phone: partnerPhone,
        partner_name: derivedPartnerName,
        email,
        phone,
      };
      await updateDoc(doc(db, 'registrations', rid), updateData);
      setReg(r => ({ ...r, ...updateData }));
      setPartnerName(derivedPartnerName);
      alert('Changes saved.');
    } catch(e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }

  async function reactivateRegistration() {
    if (!window.confirm('Reactivate this registration? It will return to active status.')) return;
    try {
      await updateDoc(doc(db, 'registrations', rid), {
        status: 'active',
        reactivated_at: new Date().toISOString(),
      });
      setReg(r => ({ ...r, status: 'active' }));
      alert('Registration reactivated.');
    } catch(e) { alert('Reactivate failed: ' + e.message); }
  }

  async function savePaymentStatus(status) {
    setSavingPayment(true);
    try {
      await updateDoc(doc(db, 'registrations', rid), { payment_status: status });
      setPaymentStatus(status);
      setReg(r => ({ ...r, payment_status: status }));
    } catch(e) { alert('Failed to update payment status: ' + e.message); }
    finally { setSavingPayment(false); }
  }

  async function moveDivision() {
    if (newDivision === reg.division) { alert('Select a different division.'); return; }
    if (!window.confirm(`Move to "${newDivision}"?`)) return;
    try {
      await updateDoc(doc(db, 'registrations', rid), { division: newDivision });
      setReg(r => ({ ...r, division: newDivision }));
      alert('Moved to ' + newDivision);
    } catch(e) { alert('Move failed: ' + e.message); }
  }

  async function cancelRegistration() {
    if (!window.confirm('Cancel this registration? This cannot be undone.')) return;
    setCancelling(true);
    try {
      const res = await authedFetch(`${FUNCTIONS_BASE}/adminCancelRegistration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reg_id: rid, issue_refund: issueRefund }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Cancel failed.'); return; }
      alert(data.message || 'Registration cancelled.');
      navigate('/tournament-admin/registrations');
    } catch(e) { alert('Cancel failed: ' + e.message); }
    finally { setCancelling(false); }
  }

  async function resendConfirmation() {
    if (!reg?.email) { alert('This registration has no email on file.'); return; }
    if (!window.confirm(`Resend confirmation email to ${reg.email}?`)) return;
    setResending(true);
    try {
      const res = await authedFetch(`${FUNCTIONS_BASE}/adminResendConfirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reg_id: rid }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Resend failed.'); return; }
      alert(data.message || 'Confirmation email sent.');
    } catch(e) { alert('Resend failed: ' + e.message); }
    finally { setResending(false); }
  }

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!reg) return (
    <Container sx={{ textAlign: 'center', py: 8 }}>
      <Typography color="text.secondary">Registration not found.</Typography>
      <Button sx={{ mt: 2 }} onClick={() => navigate('/tournament-admin/registrations')}>Back</Button>
    </Container>
  );

  const isCancelled = reg.status === 'cancelled';
  const hasPaid = reg.stripe_session_id && reg.amount_paid > 0 && (reg.payment_status === 'paid' || reg.payment_status === 'half_paid');

  const statusAlert = isCancelled
    ? <Alert severity="error" sx={{ mb: 2 }}
        action={<Button color="inherit" size="small" onClick={reactivateRegistration}>Reactivate</Button>}>
        This registration has been cancelled.
      </Alert>
    : reg.payment_status === 'paid'
    ? <Alert severity="success" sx={{ mb: 2 }}>Paid in full — ${(reg.amount_paid/100).toFixed(2)}</Alert>
    : reg.payment_status === 'half_paid'
    ? <Alert severity="warning" sx={{ mb: 2 }}>Half paid — ${(reg.amount_paid/100).toFixed(2)}</Alert>
    : <Alert severity="info" sx={{ mb: 2 }}>Pay Later — no payment collected yet</Alert>;

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" fontWeight="bold">
            ✏️ Manage Registration <Typography component="span" variant="body1" sx={{ opacity: 0.6 }}>#{rid.slice(0,8)}</Typography>
          </Typography>
          <Button variant="outlined" color="inherit" startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/tournament-admin/registrations')}>Back</Button>
        </Container>
      </Box>
      <Container sx={{ pb: 6 }}>
        {statusAlert}
        {!isCancelled && reg.waitlist_position > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>Waitlist Position #{reg.waitlist_position}</Alert>
        )}
        {!isCancelled && !reg.waitlist_position && (
          <Alert severity="info" sx={{ mb: 2 }}>Main Draw — {reg.tournament_name}, {reg.division}</Alert>
        )}

        <Grid container spacing={3}>
          {/* Edit player */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography fontWeight="bold">👤 Team Information</Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <form onSubmit={savePlayer}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a2940', mb: 1 }}>Player 1</Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} required /></Grid>
                    <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} required /></Grid>
                    <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} /></Grid>
                    <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="Phone" value={phone} onChange={e => setPhone(e.target.value)} /></Grid>
                  </Grid>

                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1a2940', mb: 1 }}>Player 2 / Partner</Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="First Name" value={partnerFirstName} onChange={e => setPartnerFirstName(e.target.value)} /></Grid>
                    <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Last Name" value={partnerLastName} onChange={e => setPartnerLastName(e.target.value)} /></Grid>
                    <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="Email" type="email" value={partnerEmail} onChange={e => setPartnerEmail(e.target.value)} /></Grid>
                    <Grid size={{ xs: 12 }}><TextField fullWidth size="small" label="Phone" value={partnerPhone} onChange={e => setPartnerPhone(e.target.value)} /></Grid>
                    {/* Legacy combined field — only show if a partner_name is set but no first/last */}
                    {partnerName && !partnerFirstName && !partnerLastName && (
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth size="small" label="Legacy Team Name" value={partnerName} onChange={e => setPartnerName(e.target.value)}
                          helperText="Older registration — split into First/Last above on save" />
                      </Grid>
                    )}
                  </Grid>

                  <Button type="submit" variant="contained" startIcon={saving ? null : <SaveIcon />} disabled={saving} sx={{ mt: 2 }}>
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>

          {/* Right column */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {divisions.length > 0 && (
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography fontWeight="bold">⇄ Move to Another Division</Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Currently in: <strong>{reg.division}</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      <FormControl size="small" sx={{ flexGrow: 1 }}>
                        <InputLabel>Move to Division</InputLabel>
                        <Select value={newDivision} label="Move to Division" onChange={e => setNewDivision(e.target.value)}>
                          {divisions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <Button variant="contained" color="info" startIcon={<SwapHorizIcon />} onClick={moveDivision}>Move</Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {true && (
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography fontWeight="bold">💳 Payment Status</Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Current: <strong>{paymentStatus === 'paid' ? 'Paid in Full' : paymentStatus === 'half_paid' ? 'Half Paid' : paymentStatus === 'pay_later' ? 'Pay Later' : paymentStatus || 'Not Set'}</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {['paid', 'half_paid', 'pay_later'].map(s => (
                        <Button key={s} size="small" variant={paymentStatus === s ? 'contained' : 'outlined'}
                          disabled={savingPayment}
                          onClick={() => savePaymentStatus(s)}
                          sx={{
                            backgroundColor: paymentStatus === s ? (s === 'paid' ? '#27AE60' : s === 'half_paid' ? '#E67E22' : '#1E3A5F') : 'transparent',
                            borderColor: s === 'paid' ? '#27AE60' : s === 'half_paid' ? '#E67E22' : '#1E3A5F',
                            color: paymentStatus === s ? 'white' : (s === 'paid' ? '#27AE60' : s === 'half_paid' ? '#E67E22' : '#1E3A5F'),
                            '&:hover': { opacity: 0.85 },
                          }}>
                          {s === 'paid' ? '✓ Paid in Full' : s === 'half_paid' ? '½ Half Paid' : '⏳ Pay Later'}
                        </Button>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {!isCancelled && (
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography fontWeight="bold">📧 Email</Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Resends the registration confirmation email (includes the online pay link if payment is still pending).
                    </Typography>
                    <Button variant="outlined" disabled={resending || !reg.email}
                      onClick={resendConfirmation}>
                      {resending ? <CircularProgress size={20} /> : 'Resend Confirmation Email'}
                    </Button>
                    {!reg.email && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                        No email on file for this registration.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {!isCancelled && (
                <Card elevation={2} sx={{ borderRadius: 3 }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography fontWeight="bold" color="error.main">✗ Cancel Registration</Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    {hasPaid ? (
                      <FormControlLabel
                        control={<Checkbox checked={issueRefund} onChange={e => setIssueRefund(e.target.checked)} />}
                        label={<Typography variant="body2">Issue Stripe refund (${(reg.amount_paid/100).toFixed(2)} back to card)</Typography>}
                        sx={{ mb: 2 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No Stripe payment on file — no refund will be processed.</Typography>
                    )}
                    <Button variant="contained" color="error" startIcon={cancelling ? null : <CancelIcon />}
                            disabled={cancelling} onClick={cancelRegistration}>
                      {cancelling ? <CircularProgress size={20} color="inherit" /> : 'Cancel Registration'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
